/**
 * BrandColorPicker — input de color de marca con:
 *   - Color picker nativo (input[type=color])
 *   - Campo hex sincronizado
 *   - Validación WCAG AA + colisión semántica en vivo
 *   - 6 sugerencias rápidas (colores probados que funcionan)
 *   - Preview: botón primario + chip activo + underline tab
 *
 * Se controla via prop `value` (hex string) + `onChange`.
 * El `error` que emite es null cuando el color es válido — el padre
 * puede leerlo para deshabilitar el botón "Guardar" si lo desea.
 */
import { useEffect, useState } from 'react';
import { readableTextOn, validateBrandColor, withAlpha } from '../utils/color';

const SUGGESTIONS = [
  '#1C5D82', // pagafacil azul (Cines Alba)
  '#C2683E', // pagafacil naranja (Propina)
  '#5E7B3E', // pagafacil verde (Coordina)
  '#8D3B72', // burdeos
  '#2E7D6B', // pino
  '#B84A28', // teja
];

interface Props {
  value: string;
  onChange: (hex: string) => void;
  onValidationChange?: (isValid: boolean) => void;
  label?: string;
}

export default function BrandColorPicker({ value, onChange, onValidationChange, label = 'Color de acento' }: Props) {
  const [localHex, setLocalHex] = useState(value || '');

  useEffect(() => setLocalHex(value || ''), [value]);

  const trimmed = localHex.trim();
  const validation = trimmed ? validateBrandColor(trimmed) : { ok: true as const };
  const isValid = validation.ok;
  const effective = isValid && trimmed ? trimmed : '#1E2A2E';
  const ink = readableTextOn(effective);
  const soft = withAlpha(effective, 0.09);

  useEffect(() => {
    onValidationChange?.(isValid);
  }, [isValid, onValidationChange]);

  const commit = (hex: string) => {
    setLocalHex(hex);
    onChange(hex);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-end gap-3">
        <div className="flex-1">
          <div className="font-mono uppercase text-[10.5px] tracking-widest text-ink-500 mb-1">
            {label}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(trimmed) ? trimmed : '#1E2A2E'}
              onChange={(e) => commit(e.target.value.toUpperCase())}
              className="w-10 h-10 rounded border border-line cursor-pointer bg-transparent"
              aria-label={`${label} · picker`}
            />
            <input
              type="text"
              value={localHex}
              onChange={(e) => commit(e.target.value)}
              placeholder="#1C5D82"
              className="flex-1 px-3 py-2 border border-line rounded font-mono text-[13px] uppercase focus:outline-none focus:border-ink-700"
              maxLength={7}
              aria-label={`${label} · hex`}
            />
          </div>
        </div>
      </div>

      {/* Sugerencias rápidas */}
      <div className="flex gap-2 flex-wrap">
        <span className="font-mono uppercase text-[10px] tracking-widest text-ink-500 self-center mr-1">
          Sugerencias:
        </span>
        {SUGGESTIONS.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => commit(c)}
            className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${trimmed.toUpperCase() === c ? 'border-ink' : 'border-line'}`}
            style={{ background: c }}
            title={c}
            aria-label={`Usar color ${c}`}
          />
        ))}
      </div>

      {/* Feedback de validación */}
      {trimmed && 'reason' in validation && (
        <div className="text-[12px] text-[#B84A28] bg-[#B84A28]/8 border border-[#B84A28]/30 px-3 py-2 rounded">
          {validation.reason}
        </div>
      )}

      {/* Preview vivo — cómo se ve tu marca en la app */}
      {isValid && (
        <div className="border border-line rounded-lg overflow-hidden">
          <div className="font-mono uppercase text-[10px] tracking-widest text-ink-500 px-3 py-2 bg-paper border-b border-line">
            Vista previa · cómo se verá tu marca
          </div>
          <div className="p-4 bg-white space-y-3">
            {/* Botón primario */}
            <div className="flex items-center gap-3">
              <button
                type="button"
                style={{ background: effective, color: ink }}
                className="px-5 py-2.5 rounded font-semibold text-[14px]"
              >
                Reservar entrada
              </button>
              <span className="text-[11px] font-mono uppercase text-ink-500">botón primario</span>
            </div>

            {/* Chip activo */}
            <div className="flex items-center gap-3">
              <span
                style={{ background: soft, color: effective, border: `1px solid ${effective}` }}
                className="px-3 py-1 rounded-full text-[12px] font-semibold"
              >
                Confirmada
              </span>
              <span className="text-[11px] font-mono uppercase text-ink-500">chip de estado</span>
            </div>

            {/* Tab activo */}
            <div className="flex items-center gap-3">
              <div className="flex gap-4">
                <span
                  style={{ borderBottom: `3px solid ${effective}`, color: effective }}
                  className="pb-1 font-semibold text-[13px]"
                >
                  Eventos
                </span>
                <span className="pb-1 text-[13px] text-ink-500 border-b-[3px] border-transparent">
                  Organizadores
                </span>
              </div>
              <span className="text-[11px] font-mono uppercase text-ink-500">tab activo</span>
            </div>

            {/* Header oscuro con logo bullet */}
            <div className="flex items-center gap-3">
              <div className="bg-[#0a0a0a] px-4 py-2 rounded flex items-center gap-2">
                <span style={{ background: effective }} className="w-2.5 h-2.5 rounded-full inline-block" />
                <span className="text-white font-display font-bold text-[13px] uppercase tracking-wider">Pagafácil</span>
              </div>
              <span className="text-[11px] font-mono uppercase text-ink-500">header oscuro</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
